import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Marketplace from "@/pages/Marketplace";
import AuctionDetail from "@/pages/AuctionDetail";
import SellItem from "@/pages/SellItem";
import MyAuctions from "@/pages/MyAuctions";
import UpcomingAuctions from "@/pages/UpcomingAuctions";
import Shops from "@/pages/Shops";
import ShopPage from "@/pages/ShopPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CourseBuilder from "@/pages/CourseBuilder";
import MyTeaching from "@/pages/MyTeaching";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import Account from "@/pages/Account";
import Messages from "@/pages/Messages";
import Vouch from "@/pages/Vouch";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import HustlersCampus from "@/pages/HustlersCampus";
import CourseDetail from "@/pages/CourseDetail";
import TrustPage from "@/pages/TrustPage";
import InvitePage from "@/pages/InvitePage";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E14] text-slate-400">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Capture a referral code from ?ref=… so it survives sign-up (email or Google).
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("hh_ref", ref);
  }, [location.search]);

  // Lightweight traffic ping — once per browser session, bots filtered server-side.
  useEffect(() => {
    if (sessionStorage.getItem("hh_traffic_pinged")) return;
    sessionStorage.setItem("hh_traffic_pinged", "1");
    api.post("/traffic/hit").catch(() => {});
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HustlersCampus />} />
      <Route path="/auctions-legacy" element={<Landing />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/campus" element={<HustlersCampus />} />
      <Route path="/campus/:courseId" element={<CourseDetail />} />
      <Route path="/teach" element={<Protected><CourseBuilder /></Protected>} />
      <Route path="/teaching" element={<Protected><MyTeaching /></Protected>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/upcoming" element={<UpcomingAuctions />} />
      <Route path="/auction/:auctionId" element={<AuctionDetail />} />
      <Route path="/shop/:slug" element={<ShopPage />} />
      <Route path="/sell" element={<Protected><SellItem /></Protected>} />
      <Route path="/my-auctions" element={<Protected><MyAuctions /></Protected>} />
      <Route path="/shops" element={<Protected><Shops /></Protected>} />
      <Route path="/account" element={<Protected><Account /></Protected>} />
      <Route path="/invite" element={<Protected><InvitePage /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/vouch/:token" element={<Vouch />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" theme="dark" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
